import { gql } from "@apollo/client";

// ============================================================================
// Queries
// ============================================================================

export const GET_EMAIL_SETTINGS = gql`
  query EmailSettings {
    emailSettings {
      id
      enabled
      provider
      fromAddress
      fromName
      smtpHost
      smtpPort
      smtpSecure
      smtpUser
      smtpPasswordSet
      resendConfigured
      smtpConfigured
      updatedAt
      updatedById
    }
  }
`;

export const GET_EMAIL_TEMPLATES = gql`
  query EmailTemplates {
    emailTemplates {
      id
      key
      name
      subject
      bodyHtml
      enabled
      availableVariables
      updatedAt
    }
  }
`;

export const PREVIEW_EMAIL_TEMPLATE = gql`
  query PreviewEmailTemplate($input: PreviewEmailTemplateInput!) {
    previewEmailTemplate(input: $input) {
      subject
      html
      text
    }
  }
`;

export const GET_EMAIL_LOGS = gql`
  query EmailLogs($filter: EmailLogFilterInput) {
    emailLogs(filter: $filter) {
      items {
        id
        to
        subject
        bodyHtml
        plainText
        templateKey
        status
        errorMessage
        attemptCount
        nextRetryAt
        sentAt
        createdAt
        contextType
        contextId
      }
      total
      page
      pageSize
    }
  }
`;

// ============================================================================
// Mutations
// ============================================================================

export const UPDATE_EMAIL_SETTINGS = gql`
  mutation UpdateEmailSettings($input: UpdateEmailSettingsInput!) {
    updateEmailSettings(input: $input) {
      id
      enabled
      provider
      fromAddress
      fromName
      smtpHost
      smtpPort
      smtpSecure
      smtpUser
      smtpPasswordSet
      resendConfigured
      smtpConfigured
      updatedAt
    }
  }
`;

export const UPDATE_EMAIL_TEMPLATE = gql`
  mutation UpdateEmailTemplate($input: UpdateEmailTemplateInput!) {
    updateEmailTemplate(input: $input) {
      id
      key
      name
      subject
      bodyHtml
      enabled
      availableVariables
      updatedAt
    }
  }
`;

export const SEND_TEST_EMAIL = gql`
  mutation SendTestEmail($input: SendTestEmailInput!) {
    sendTestEmail(input: $input) {
      logId
    }
  }
`;

export const RETRY_EMAIL_LOG = gql`
  mutation RetryEmailLog($id: String!) {
    retryEmailLog(id: $id) {
      id
      status
      attemptCount
      errorMessage
    }
  }
`;

// ============================================================================
// Types (mirror backend GraphQL types)
// ============================================================================

export type EmailProvider = "RESEND" | "SMTP";

export type EmailTemplateKey =
  | "PROJECT_MEMBER_ADDED"
  | "TASK_ASSIGNED"
  | "PROJECT_STATUS_CHANGED"
  | "TASK_STATUS_CHANGED"
  | "TASK_COMMENT_ADDED";

export type EmailLogStatus = "PENDING" | "SENT" | "FAILED" | "SKIPPED_DISABLED";

export interface EmailSettings {
  id: string;
  enabled: boolean;
  provider: EmailProvider;
  fromAddress: string;
  fromName: string;
  smtpHost: string | null;
  smtpPort: number | null;
  smtpSecure: boolean;
  smtpUser: string | null;
  smtpPasswordSet: boolean;
  resendConfigured: boolean;
  smtpConfigured: boolean;
  updatedAt: string;
  updatedById: string | null;
}

export interface EmailTemplate {
  id: string;
  key: EmailTemplateKey;
  name: string;
  subject: string;
  bodyHtml: string;
  enabled: boolean;
  availableVariables: string[];
  updatedAt: string;
}

export interface EmailLog {
  id: string;
  to: string;
  subject: string;
  bodyHtml: string;
  plainText: string | null;
  templateKey: EmailTemplateKey | null;
  status: EmailLogStatus;
  errorMessage: string | null;
  attemptCount: number;
  nextRetryAt: string | null;
  sentAt: string | null;
  createdAt: string;
  contextType: string | null;
  contextId: string | null;
}

export interface EmailLogPage {
  items: EmailLog[];
  total: number;
  page: number;
  pageSize: number;
}

export interface EmailPreview {
  subject: string;
  html: string;
  text: string;
}
